// manual-subscription-billing — WU8: RecordManualPaymentDialog.
//
// Super Admin records an offline payment against a specific OPEN manual
// SubscriptionInvoice (design §3.3, spec Capability 4 —
// POST /super-admin/organizations/:orgId/subscription/invoices/:invoiceId/record-payment).
// Mirrors AssignPlanDialog's controlled-form + ApiError-message pattern.

import { ApiError, recordManualSubscriptionPayment } from '@jq4race/api-client';
import { Trans, useTranslation } from '@jq4race/i18n';
import type {
  ManualPaymentMethodDto,
  RecordManualSubscriptionPaymentResponse,
  SubscriptionInvoiceResponse,
} from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  formatCents,
  Input,
  Label,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

const PAYMENT_METHOD_KEYS: Array<{ value: ManualPaymentMethodDto; labelKey: string }> = [
  { value: 'bank_transfer', labelKey: 'recordPaymentDialog.paymentMethod.bankTransfer' },
  { value: 'check', labelKey: 'recordPaymentDialog.paymentMethod.check' },
  { value: 'cash', labelKey: 'recordPaymentDialog.paymentMethod.cash' },
  { value: 'other', labelKey: 'recordPaymentDialog.paymentMethod.other' },
];

interface RecordManualPaymentDialogProps {
  orgId: string;
  invoice: SubscriptionInvoiceResponse | null;
  onOpenChange: (open: boolean) => void;
  onRecorded: (result: RecordManualSubscriptionPaymentResponse) => void;
}

export function RecordManualPaymentDialog({
  orgId,
  invoice,
  onOpenChange,
  onRecorded,
}: RecordManualPaymentDialogProps) {
  const { t } = useTranslation('organizations');
  const open = invoice !== null;
  const [paymentMethod, setPaymentMethod] = useState<ManualPaymentMethodDto>('bank_transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPaymentMethod('bank_transfer');
      setPaymentReference('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!invoice?.id) return;

    setSubmitting(true);
    setError(null);
    try {
      const trimmedReference = paymentReference.trim();
      const result = await recordManualSubscriptionPayment(orgId, invoice.id, {
        paymentMethod,
        ...(trimmedReference ? { paymentReference: trimmedReference } : {}),
      });
      onRecorded(result);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(translateApiError(err, t, { fallback: t('recordPaymentDialog.error') }));
      } else {
        setError(translateApiError(err, t, { fallback: t('recordPaymentDialog.error') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('recordPaymentDialog.title')}</DialogTitle>
          <DialogDescription>
            {invoice ? (
              <Trans
                t={t}
                i18nKey={
                  invoice.invoiceNumber
                    ? 'recordPaymentDialog.descriptionWithNumber'
                    : 'recordPaymentDialog.descriptionWithoutNumber'
                }
                values={{
                  amount: formatCents(invoice.amountCents, invoice.currency),
                  number: invoice.invoiceNumber,
                }}
                components={{ strong: <strong className="font-medium text-jq-charcoal" /> }}
              />
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="record-payment-method">
              {t('recordPaymentDialog.paymentMethodLabel')}
            </Label>
            <select
              id="record-payment-method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as ManualPaymentMethodDto)}
              disabled={submitting}
              className="h-10 w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50"
            >
              {PAYMENT_METHOD_KEYS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="record-payment-reference">
              {t('recordPaymentDialog.paymentReferenceLabel')}
            </Label>
            <Input
              id="record-payment-reference"
              placeholder={t('recordPaymentDialog.paymentReferencePlaceholder')}
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              disabled={submitting}
              maxLength={200}
            />
          </div>

          {error && (
            <p className="rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('recordPaymentDialog.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? t('recordPaymentDialog.submitting') : t('recordPaymentDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
